/**
 * Product Card Selector Component
 *
 * Horizontal grid of product cards for selection.
 * Integrates with react-hook-form for form state management.
 * On mobile, includes action sheet for product details.
 */

import { AlertCircleIcon } from 'lucide-react-native';
import { useState } from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { View } from 'react-native';

import { ProductSelectDropdown } from './ProductSelectDropdown';

import { ProductSelectCard } from '@/components/MedicalOnDemand/form/ProductSelectCard';
import { ProductDetailsSheet } from '@/components/MedicalOnDemand/mobile/ProductDetailsSheet';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { type Product } from '@/constants/medicalOnDemand';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { MODFormData } from '@/hooks/useMODInquiryForm';

type ProductSelectorProps = {
  products: Product[];
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
};

export function ProductSelector({
  products,
  control,
  errors,
  isDisabled = false,
}: ProductSelectorProps) {
  const [detailsProduct, setDetailsProduct] = useState<Product | null>(null);

  const isModProductFeatured = useFeatureFlag(FEATURE_FLAGS.MOD_PRODUCT_FEATURED);

  const handleShowDetails = (product: Product) => {
    setDetailsProduct(product);
  };

  const handleCloseDetails = () => {
    setDetailsProduct(null);
  };

  return (
    <>
      <Controller
        name="productIds"
        control={control}
        render={({ field: { onChange, value }, fieldState, formState }) => {
          const showError =
            !!errors.productIds?.message && (fieldState.isTouched || formState.isSubmitted);

          const toggleProduct = (productId: string) => {
            const current = (value ?? []) as string[];

            const nextSelectedIds = current.includes(productId)
              ? current.filter((id) => id !== productId)
              : [...current, productId];

            onChange(nextSelectedIds);
          };

          if (isModProductFeatured) {
            return (
              <FormControl isInvalid={showError} isDisabled={isDisabled}>
                <HStack className="mb-2 items-center justify-between">
                  <Text className="font-semibold text-sm text-typography-950">Product(s)*</Text>
                  <Text className="text-xs text-typography-500">
                    * Required (select one or more)
                  </Text>
                </HStack>

                <View className="flex flex-col gap-4 md:grid md:grid-cols-2 lg:grid lg:grid-cols-3">
                  {products.map((product) => (
                    <ProductSelectCard
                      key={product.id}
                      product={product}
                      isSelected={((value ?? []) as string[]).includes(product.id)}
                      isDisabled={isDisabled}
                      onSelect={() => toggleProduct(product.id)}
                      onShowDetails={() => handleShowDetails(product)}
                    />
                  ))}
                </View>

                <FormControlError className="mt-3">
                  <FormControlErrorIcon as={AlertCircleIcon} />
                  <FormControlErrorText size="xs">
                    {errors.productIds?.message}
                  </FormControlErrorText>
                </FormControlError>
              </FormControl>
            );
          } else {
            return (
              <ProductSelectDropdown
                products={products}
                control={control}
                errors={errors}
                isDisabled={isDisabled}
              />
            );
          }
        }}
      />

      <ProductDetailsSheet
        isOpen={!!detailsProduct}
        onClose={handleCloseDetails}
        product={detailsProduct}
      />
    </>
  );
}
